import {
    DisciplineEvaluationType,
    EventFormat,
    eventFormatToDisciplineCategoriesMap,
    formatDisciplineEvaluationType,
    formatUnit,
} from "@bujus/common";
import {
    Card,
    CardContent,
    Dialog,
    DialogContent,
    DialogTitle,
    Stack,
    Typography,
} from "@mui/material";

import { DialogClosingButton } from "@/base";
import { useSelectedEventStateContext } from "@/contexts";

const formatDisciplineCalculation = (eventFormat, discipline) => {
    switch (eventFormat) {
        case EventFormat.CONTEST:
            switch (discipline.evaluationType) {
                case DisciplineEvaluationType.ADD_ALL:
                    return `Es sind ${discipline.tryLimit} Versuche vorgesehen. Für die Wertung werden die besten gültigen Werte bis zu diesem Limit addiert.`;
                case DisciplineEvaluationType.ADD_TOP_3:
                    return `Es sind ${discipline.tryLimit} Versuche vorgesehen. Für die Wertung werden die 3 besten gültigen Werte addiert.`;
                case DisciplineEvaluationType.BEST:
                    return `Es sind bis zu ${discipline.tryLimit} Versuche vorgesehen. Für die Wertung zählt der beste gültige Wert.`;
                case DisciplineEvaluationType.SINGLE:
                    return "Es ist ein Einzelergebnis vorgesehen.";
                default:
                    throw new Error(
                        `Invalid discipline evaluation type: ${discipline.evaluationType}`,
                    );
            }
        case EventFormat.COMPETITION:
            return "Für die Urkunde zählt der gültige Wert mit der höchsten Punktzahl in der Disziplinkategorie.";
        default:
            throw new Error(`Invalid event format: ${eventFormat}`);
    }
};

const formatDisciplineOverflowBehavior = (eventFormat, discipline) => {
    switch (eventFormat) {
        case EventFormat.CONTEST:
            switch (discipline.evaluationType) {
                case DisciplineEvaluationType.ADD_ALL:
                    return "Mehr Einträge werden technisch nicht blockiert. Für die Wertung verwendet das System trotzdem nur die besten gültigen Werte bis zum Versuchslimit.";
                case DisciplineEvaluationType.ADD_TOP_3:
                    return "Mehr Einträge werden technisch nicht blockiert. Für die Wertung verwendet das System trotzdem nur die 3 besten gültigen Werte.";
                case DisciplineEvaluationType.BEST:
                    return "Mehr Einträge werden technisch nicht blockiert. Für die Wertung verwendet das System trotzdem nur den besten gültigen Wert.";
                case DisciplineEvaluationType.SINGLE:
                    return "Mehr Einträge werden technisch nicht blockiert. Für die Wertung verwendet das System trotzdem nur den besten gültigen Wert.";
                default:
                    throw new Error(
                        `Invalid discipline evaluation type: ${discipline.evaluationType}`,
                    );
            }
        case EventFormat.COMPETITION:
            return "Mehr Einträge werden technisch nicht blockiert. Für die Urkunde kann trotzdem das beste gültige Ergebnis der Disziplinkategorie zählen.";
        default:
            throw new Error(`Invalid event format: ${eventFormat}`);
    }
};

const DisciplineOverviewDialog = ({ close, isOpen }) => {
    const [selectedEvent] = useSelectedEventStateContext();

    const disciplineCategories =
        eventFormatToDisciplineCategoriesMap[selectedEvent.format];

    const handleClose = () => {
        close();
    };

    const handleClosingButtonClick = () => {
        close();
    };

    return (
        <Dialog maxWidth="lg" onClose={handleClose} open={isOpen}>
            <DialogTitle>Disziplinen Überblick</DialogTitle>
            <DialogClosingButton onClick={handleClosingButtonClick} />
            <DialogContent>
                <Stack spacing={2}>
                    {disciplineCategories.map((disciplineCategory) => (
                        <Card key={disciplineCategory.id} variant="outlined">
                            <CardContent>
                                <Typography variant="h6">
                                    {disciplineCategory.name}
                                </Typography>
                                <Stack spacing={2} mt={1}>
                                    {disciplineCategory.disciplines.map((discipline) => (
                                        <Card sx={{ height: "100%" }} variant="outlined" key={discipline.id}>
                                            <CardContent>
                                                <Stack spacing={1}>
                                                    <Typography variant="h6">
                                                        {discipline.name}
                                                    </Typography>
                                                    <Typography>
                                                        <b>Regel:</b>{" "}
                                                        {formatDisciplineCalculation(
                                                            selectedEvent.format,
                                                            discipline,
                                                        )}
                                                    </Typography>
                                                    <Typography>
                                                        <b>Bei mehr Einträgen:</b>{" "}
                                                        {formatDisciplineOverflowBehavior(
                                                            selectedEvent.format,
                                                            discipline,
                                                        )}
                                                    </Typography>
                                                    <Typography>
                                                        <b>Versuchslimit:</b> {discipline.tryLimit}
                                                    </Typography>
                                                    <Typography>
                                                        <b>Eingabeeinheit:</b>{" "}
                                                        {formatUnit(discipline.inputUnit)}
                                                    </Typography>
                                                    <Typography>
                                                        <b>Wertungseinheit:</b>{" "}
                                                        {formatUnit(discipline.unit)}
                                                    </Typography>
                                                    <Typography>
                                                        <b>Bewertungstyp:</b>{" "}
                                                        {formatDisciplineEvaluationType(
                                                            discipline.evaluationType,
                                                        )}
                                                    </Typography>
                                                    {discipline.note !== undefined && (
                                                        <Typography>
                                                            <b>Anweisung:</b> {discipline.note}
                                                        </Typography>
                                                    )}
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>
                    ))}
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

export { DisciplineOverviewDialog };
